import { chaikin, type SketchPoint } from "./stroke-points";

/** A streamlined user ink point in screen points, with final pressure. */
export type InkPoint = { x: number; y: number; p: number; tMs?: number };

type RawInkPoint = { x: number; y: number; tMs?: number };

const FIRST_POINT_PRESSURE = 0.9;
const STREAMLINE = 0.45;
const MIN_STEP_PT = 1;
const PRESSURE_SMOOTHING = 0.7;

/** Runs once per pointer event. Pulls the stroke 45 % toward the pointer,
 * drops moves under 1 pt, and thins the ink for fast motion: the pressure
 * target is clamp(1.15 − v·0.35, 0.5, 1.1) with v in pt/ms (1 without time). */
export function streamlineInkPoint(
  previous: InkPoint | null,
  raw: RawInkPoint,
): InkPoint | null {
  if (!previous) {
    return { x: raw.x, y: raw.y, p: FIRST_POINT_PRESSURE, tMs: raw.tMs };
  }
  const x = previous.x + (raw.x - previous.x) * STREAMLINE;
  const y = previous.y + (raw.y - previous.y) * STREAMLINE;
  const moved = Math.hypot(x - previous.x, y - previous.y);
  if (moved < MIN_STEP_PT) return null;
  let target = 1;
  if (raw.tMs !== undefined && previous.tMs !== undefined) {
    const velocity = moved / Math.max(1, raw.tMs - previous.tMs);
    target = Math.max(0.5, Math.min(1.1, 1.15 - velocity * 0.35));
  }
  return {
    x,
    y,
    p: previous.p * PRESSURE_SMOOTHING + target * (1 - PRESSURE_SMOOTHING),
    tMs: raw.tMs,
  };
}

/** The points one pointer event stores: the streamlined step (none for a
 * move under 1 pt), and on release also the raw release point itself, so a
 * fast stroke ends where the pen lifted rather than short of it. The release
 * point carries the smoothed pressure and is skipped within 1 pt of the last
 * stored point. */
export function storedInkPoints(
  previous: InkPoint | null,
  raw: RawInkPoint,
  released: boolean,
): InkPoint[] {
  const step = streamlineInkPoint(previous, raw);
  const points = step ? [step] : [];
  const last = step ?? previous;
  if (
    released &&
    last &&
    Math.hypot(raw.x - last.x, raw.y - last.y) >= MIN_STEP_PT
  ) {
    points.push({ x: raw.x, y: raw.y, p: last.p, tMs: raw.tMs });
  }
  return points;
}

/** Renders stored ink points: two Chaikin passes, pressure already final.
 * Appending a point only changes the tail, so the drawn prefix is stable. */
export function userInkStroke(points: readonly InkPoint[]): SketchPoint[] {
  return chaikin(points, 2);
}

const MAX_STROKE_POINTS = 200;
const KEPT_NEWEST_POINTS = 100;

/** Appends a stored point, thinning a full stroke (200 points) first: every
 * other point of its oldest part goes, while the first point and the newest
 * 100 stay exactly as they are. Stored points are already smooth and dense,
 * so the old ink moves by under a pixel and the live end does not move. */
export function appendInkPoint<T>(points: readonly T[], point: T): T[] {
  if (points.length < MAX_STROKE_POINTS) return [...points, point];
  const split = points.length - KEPT_NEWEST_POINTS;
  return [
    ...points.slice(0, split).filter((_point, index) => index % 2 === 0),
    ...points.slice(split),
    point,
  ];
}
